import React, { useState, useEffect } from 'react';
import api from '../api';
import { Play, CheckCircle, Clock, Camera, X } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { uploadFile } from '../utils/uploadFile';

const SERVICES = {
  'Manutenção Preventiva': [
    'Limpeza de eletrodutos', 'Limpeza de lâmpadas e luminárias', 'Limpeza de painel elétrico',
    'Limpeza de perfilados e eletrocalhas', 'Limpeza de refletores', 'Limpeza de ventiladores e ar condicionado',
    'Reaperto de contatos', 'Revisão e isolação de emendas de cabos'
  ],
  'Reforma': ['Extensão de tomada', 'Troca de lâmpada fluorescente por LED'],
  'Manutenção Corretiva': [
    'Troca de bóias elétricas', 'Troca de botões e sinaleiros', 'Troca de cabos e terminais',
    'Troca de capacitores', 'Troca de chave fim de curso', 'Troca de contatores', 'Troca de disjuntor',
    'Troca de fusíveis', 'Troca de lâmpada LED', 'Troca de plug para padrão novo',
    'Troca de resistência de chuveiro', 'Troca de sensores', 'Troca de soquete e fiação luminária',
    'Troca de tomada 20A ampéres', 'Troca de tomada elétrica em curto', 'Troca de transformador e reator'
  ],
  'Montagem': [
    'Montagem de canaletas sistema X', 'Montagem de eletrodutos', 'Montagem de perfilados e eletrocalhas',
    'Montagem de quadro de disjuntores', 'Montagem de tubulações galvanizadas',
    'Montagem e desmontagem de equipamentos elétricos'
  ],
  'Instalação': [
    'Instalação de aquecedor elétrico', 'Instalação de cabo de rede Ethernet', 'Instalação de chuveiro elétrico',
    'Instalação de compressores', 'Instalação de iluminação de emergência', 'Instalação de luminárias',
    'Instalação de lustre', 'Instalação de máquina CNC', 'Instalação de máquina convencional',
    'Instalação de máquina de lavar', 'Instalação de máquina lava e seca', 'Instalação de micro-ondas',
    'Instalação de motores e bombas', 'Instalação de pendente', 'Instalação de pressurizador para chuveiro',
    'Instalação de purificador de água', 'Instalação de sensor de presença', 'Instalação de spot',
    'Instalação de tomada 20A ampéres', 'Instalação de tomada trifásica', 'Instalação de tomadas',
    'Instalação de TV', 'Instalação de ventilador de parede', 'Instalação de ventilador de teto'
  ]
};

const AtendimentosPrestador = ({ prestadorId }) => {
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [clients, setClients] = useState([]);
  const [prestadores, setPrestadores] = useState([]);
  const [auxiliares, setAuxiliares] = useState([]);
  const [selectedSolicitacao, setSelectedSolicitacao] = useState(null);
  const [isRelatorioOpen, setIsRelatorioOpen] = useState(false);
  const [selectedServices, setSelectedServices] = useState([]);
  const [relatorioData, setRelatorioData] = useState({
    data_atendimento: new Date().toISOString().split('T')[0],
    horario_chegada: '',
    horario_saida: '',
    observacoes: '',
    eletricista_id: '',
    auxiliar_id: ''
  });
  const [fotos, setFotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    fetchSolicitacoes();
    fetchWorkers();
  }, [prestadorId]);

  const fetchSolicitacoes = async () => {
    try {
      const [solicitacoesRes, clientsRes] = await Promise.all([
        api.get(`/solicitacoes?prestador_id=${prestadorId}`),
        api.get('/clients')
      ]);
      setSolicitacoes(solicitacoesRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Error fetching solicitacoes:', error);
    }
  };

  const fetchWorkers = async () => {
    try {
      const [prestRes, auxRes] = await Promise.all([
        api.get('/prestadores'),
        api.get('/auxiliares')
      ]);
      setPrestadores(prestRes.data.filter(p => p.status === 'ativo'));
      setAuxiliares(auxRes.data.filter(a => a.status === 'ativo'));
    } catch (error) {
      console.error('Error fetching workers:', error);
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? client.name : 'N/A';
  };

  const handleIniciarAtendimento = async (solicitacao) => {
    try {
      await api.post(`/solicitacoes/${solicitacao.id}/iniciar`);
      fetchSolicitacoes();
      alert('Atendimento iniciado!');
    } catch (error) {
      alert('Erro ao iniciar atendimento: ' + (error.response?.data?.detail || error.message));
    }
  };

  const toggleService = (category, service) => {
    const exists = selectedServices.find(s => s.categoria === category && s.servico === service);
    if (exists) {
      setSelectedServices(selectedServices.filter(s => !(s.categoria === category && s.servico === service)));
    } else {
      setSelectedServices([...selectedServices, { categoria: category, servico: service }]);
    }
  };

  const handleFotoSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(f => f.type.startsWith('image/') && f.size <= 10 * 1024 * 1024);
    if (validFiles.length !== files.length) alert('Alguns arquivos foram ignorados. Aceito apenas imagens de até 10MB.');
    setFotos(prev => [...prev, ...validFiles.map(file => ({ file, preview: URL.createObjectURL(file) }))]);
  };

  const removeFoto = (index) => {
    setFotos(prev => { const u = [...prev]; URL.revokeObjectURL(u[index].preview); u.splice(index, 1); return u; });
  };

  const openRelatorioModal = (solicitacao) => {
    // Pre-select the assigned prestador as eletricista
    setRelatorioData(prev => ({
      ...prev,
      data_atendimento: new Date().toISOString().split('T')[0],
      horario_chegada: '',
      horario_saida: '',
      observacoes: '',
      eletricista_id: solicitacao.prestador_id || '',
      auxiliar_id: ''
    }));
    setSelectedSolicitacao(solicitacao);
    setSelectedServices([]);
    setFotos([]);
    setIsRelatorioOpen(true);
  };

  const handleCriarRelatorio = async (e) => {
    e.preventDefault();
    if (selectedServices.length === 0) { alert('Selecione pelo menos um serviço executado'); return; }
    if (!relatorioData.eletricista_id) { alert('Selecione o eletricista'); return; }

    try {
      setUploading(true);
      const fotoUrls = [];
      for (let i = 0; i < fotos.length; i++) {
        setUploadProgress(Math.round(((i) / fotos.length) * 100));
        const url = await uploadFile(fotos[i].file, `relatorios/${selectedSolicitacao.id}`, (p) => setUploadProgress(Math.round(((i + p / 100) / fotos.length) * 100)));
        fotoUrls.push(url);
      }
      setUploadProgress(100);

      const eletricista = prestadores.find(p => p.id === relatorioData.eletricista_id);
      const auxiliar = relatorioData.auxiliar_id ? auxiliares.find(a => a.id === relatorioData.auxiliar_id) : null;

      const dataRef = relatorioData.data_atendimento || new Date().toISOString().split('T')[0];

      await api.post(`/solicitacoes/${selectedSolicitacao.id}/relatorio`, {
        solicitacao_id: selectedSolicitacao.id,
        servicos_executados: selectedServices,
        horario_chegada: `${dataRef}T${relatorioData.horario_chegada}:00`,
        horario_saida: `${dataRef}T${relatorioData.horario_saida}:00`,
        data_atendimento: dataRef,
        observacoes: relatorioData.observacoes || null,
        fotos_antes: [],
        fotos_depois: fotoUrls,
        eletricista_id: relatorioData.eletricista_id,
        eletricista_nome: eletricista?.name || '',
        auxiliar_id: auxiliar?.id || null,
        auxiliar_nome: auxiliar?.name || null
      });

      fotos.forEach(f => URL.revokeObjectURL(f.preview));
      setIsRelatorioOpen(false);
      setSelectedSolicitacao(null);
      setSelectedServices([]);
      setFotos([]);
      fetchSolicitacoes();
      alert('Relatório enviado! Aguardando aprovação do cliente.');
    } catch (error) {
      alert('Erro ao criar relatório: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const getStatusBadge = (status) => {
    const colors = { 'atribuida': 'bg-blue-500', 'em_andamento': 'bg-purple-500', 'aguardando_aprovacao': 'bg-orange-500', 'concluida': 'bg-green-500' };
    const labels = { 'atribuida': 'Atribuída', 'em_andamento': 'Em Andamento', 'aguardando_aprovacao': 'Aguardando Aprovação', 'concluida': 'Concluída' };
    return <Badge className={colors[status]}>{labels[status]}</Badge>;
  };

  return (
    <div>
      <h3 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Barlow Condensed' }}>Atendimentos</h3>

      {solicitacoes.length === 0 ? (
        <div className="bg-[#121212] border border-[#27272A] rounded-sm p-8 text-center text-zinc-400">Nenhum atendimento atribuído no momento</div>
      ) : (
        <div className="space-y-4">
          {solicitacoes.map(solicitacao => (
            <div key={solicitacao.id} className="bg-[#121212] border border-[#27272A] rounded-sm p-6" data-testid={`atendimento-${solicitacao.numero_solicitacao}`}>
              <div className="flex justify-between items-start mb-4">
                <div>
                  <div className="text-2xl font-bold text-[#FFB800]" style={{ fontFamily: 'Barlow Condensed' }}>{solicitacao.numero_solicitacao}</div>
                  <div className="text-white font-medium mt-1">{getClientName(solicitacao.client_id)}</div>
                </div>
                {getStatusBadge(solicitacao.status)}
              </div>
              <div className="mb-4">
                <div className="text-zinc-400 text-xs mb-2">Serviços Solicitados</div>
                <div className="flex flex-wrap gap-2">
                  {solicitacao.servicos_solicitados.map((servico, idx) => (
                    <span key={idx} className="text-xs bg-[#1E1E1E] px-2 py-1 rounded text-zinc-400">{servico}</span>
                  ))}
                </div>
              </div>
              {solicitacao.descricao && (
                <div className="mb-4">
                  <div className="text-zinc-400 text-xs mb-1">Descrição</div>
                  <div className="text-white text-sm">{solicitacao.descricao}</div>
                </div>
              )}
              <div className="flex gap-3">
                {solicitacao.status === 'atribuida' && (
                  <Button onClick={() => handleIniciarAtendimento(solicitacao)} className="bg-[#FFB800] text-black hover:bg-[#E6A600]" data-testid="iniciar-atendimento-button">
                    <Play size={16} className="mr-2" />Iniciar Atendimento
                  </Button>
                )}
                {solicitacao.status === 'em_andamento' && (
                  <Button onClick={() => openRelatorioModal(solicitacao)} className="bg-[#22C55E] text-white hover:bg-[#16A34A]" data-testid="criar-relatorio-button">
                    <CheckCircle size={16} className="mr-2" />Criar Relatório
                  </Button>
                )}
                {solicitacao.status === 'aguardando_aprovacao' && (
                  <div className="flex items-center gap-2 text-orange-500"><Clock size={16} /><span className="text-sm">Aguardando aprovação do cliente</span></div>
                )}
                {solicitacao.status === 'concluida' && (
                  <div className="flex items-center gap-2 text-green-500"><CheckCircle size={16} /><span className="text-sm">Concluído - {solicitacao.horas_executadas.toFixed(1)}h</span></div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Criar Relatório */}
      <Dialog open={isRelatorioOpen} onOpenChange={setIsRelatorioOpen}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-white">Criar Relatório de Atendimento</DialogTitle></DialogHeader>
          {selectedSolicitacao && (
            <form onSubmit={handleCriarRelatorio} className="space-y-6">
              <div className="p-4 bg-[#050505] border border-[#27272A] rounded-sm">
                <div className="text-sm text-zinc-400">Solicitação</div>
                <div className="text-lg font-bold text-[#FFB800]">{selectedSolicitacao.numero_solicitacao}</div>
              </div>

              {/* Equipe */}
              <div>
                <h4 className="text-[#FFB800] font-semibold mb-3" style={{ fontFamily: 'Barlow Condensed' }}>Equipe do Atendimento</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nome Eletricista *</Label>
                    <Select value={relatorioData.eletricista_id} onValueChange={(v) => setRelatorioData({...relatorioData, eletricista_id: v})}>
                      <SelectTrigger className="bg-[#050505] border-[#27272A] text-white" data-testid="eletricista-select">
                        <SelectValue placeholder="Selecione o eletricista..." />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[#27272A] text-white">
                        {prestadores.map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Nome Auxiliar (opcional)</Label>
                    <Select value={relatorioData.auxiliar_id || '_none'} onValueChange={(v) => setRelatorioData({...relatorioData, auxiliar_id: v === '_none' ? '' : v})}>
                      <SelectTrigger className="bg-[#050505] border-[#27272A] text-white" data-testid="auxiliar-select">
                        <SelectValue placeholder="Sem auxiliar" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[#27272A] text-white">
                        <SelectItem value="_none">Sem auxiliar</SelectItem>
                        {auxiliares.map(a => (
                          <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Data do Atendimento */}
              <div>
                <Label>Data do Atendimento *</Label>
                <Input type="date" value={relatorioData.data_atendimento} onChange={(e) => setRelatorioData({...relatorioData, data_atendimento: e.target.value})} className="bg-[#050505] border-[#27272A] text-white" required data-testid="data-atendimento-input" />
              </div>

              {/* Horários */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Horário de Chegada *</Label>
                  <Input type="time" value={relatorioData.horario_chegada} onChange={(e) => setRelatorioData({...relatorioData, horario_chegada: e.target.value})} className="bg-[#050505] border-[#27272A] text-white" required data-testid="horario-chegada-input" />
                </div>
                <div>
                  <Label>Horário de Saída *</Label>
                  <Input type="time" value={relatorioData.horario_saida} onChange={(e) => setRelatorioData({...relatorioData, horario_saida: e.target.value})} className="bg-[#050505] border-[#27272A] text-white" required data-testid="horario-saida-input" />
                </div>
              </div>

              {/* Serviços Executados */}
              <div>
                <Label className="mb-3 block">Serviços Executados * (selecione o que foi feito)</Label>
                <div className="space-y-4 max-h-96 overflow-y-auto bg-[#050505] border border-[#27272A] rounded-sm p-4">
                  {Object.entries(SERVICES).map(([category, services]) => (
                    <div key={category}>
                      <h4 className="text-[#FFB800] font-semibold mb-2" style={{ fontFamily: 'Barlow Condensed' }}>{category}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 ml-4">
                        {services.map(service => (
                          <div key={service} className="flex items-center space-x-2">
                            <Checkbox id={`exec-${service}`} checked={selectedServices.some(s => s.categoria === category && s.servico === service)} onCheckedChange={() => toggleService(category, service)} className="border-[#27272A] data-[state=checked]:bg-[#FFB800] data-[state=checked]:border-[#FFB800]" />
                            <label htmlFor={`exec-${service}`} className="text-sm text-zinc-400 cursor-pointer">{service}</label>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                {selectedServices.length > 0 && <div className="mt-2 text-sm text-[#FFB800]">{selectedServices.length} serviço(s) executado(s)</div>}
              </div>

              {/* Upload de Fotos */}
              <div>
                <Label className="mb-3 block">Fotos do Serviço</Label>
                <div className="bg-[#050505] border border-[#27272A] rounded-sm p-4">
                  <label className="flex flex-col items-center justify-center border-2 border-dashed border-[#27272A] rounded-sm p-6 cursor-pointer hover:border-[#FFB800] transition-colors" data-testid="foto-upload-area">
                    <Camera size={32} className="text-zinc-500 mb-2" />
                    <span className="text-sm text-zinc-400">Clique para adicionar fotos</span>
                    <span className="text-xs text-zinc-600 mt-1">JPG, PNG - máx 10MB por foto</span>
                    <input type="file" accept="image/*" multiple onChange={handleFotoSelect} className="hidden" data-testid="foto-file-input" />
                  </label>
                  {fotos.length > 0 && (
                    <div className="grid grid-cols-3 gap-3 mt-4">
                      {fotos.map((foto, idx) => (
                        <div key={idx} className="relative group">
                          <img src={foto.preview} alt={`Foto ${idx + 1}`} className="w-full h-24 object-cover rounded-sm border border-[#27272A]" />
                          <button type="button" onClick={() => removeFoto(idx)} className="absolute top-1 right-1 bg-red-600 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"><X size={14} className="text-white" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Observações */}
              <div>
                <Label>Observações</Label>
                <textarea value={relatorioData.observacoes} onChange={(e) => setRelatorioData({...relatorioData, observacoes: e.target.value})} className="w-full px-4 py-2 bg-[#050505] border border-[#27272A] rounded-sm text-white focus:border-[#FFB800] focus:ring-1 focus:ring-[#FFB800] outline-none min-h-24" placeholder="Descreva detalhes do atendimento..." data-testid="observacoes-textarea" />
              </div>

              {uploading && (
                <div className="p-4 bg-[#050505] border border-[#27272A] rounded-sm">
                  <div className="text-sm text-zinc-400 mb-2">Enviando fotos... {uploadProgress}%</div>
                  <div className="w-full bg-[#27272A] rounded-full h-2"><div className="bg-[#FFB800] h-2 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} /></div>
                </div>
              )}

              <div className="flex gap-3">
                <Button type="button" onClick={() => setIsRelatorioOpen(false)} className="flex-1 bg-[#1E1E1E] hover:bg-[#27272A]" disabled={uploading}>Cancelar</Button>
                <Button type="submit" className="flex-1 bg-[#FFB800] text-black hover:bg-[#E6A600]" disabled={uploading} data-testid="submit-relatorio-button">{uploading ? 'Enviando...' : 'Enviar Relatório'}</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AtendimentosPrestador;
